'use client'

import CompanyBlocker from '@/app/[lng]/components/BlockerModal/CompanyBlocker'
import { useTranslation } from '@/app/i18n/client'
import { Skeleton } from '@/components/ui/skeleton'
import { getCompanyProfileData } from '@/services/companyDetailsService'
import React, { useEffect, useState } from 'react'
import ProductOrServiceSection from './ProductOrServiceSection'
import { useSelector } from 'react-redux'
import { RootState } from '@/app/globalRedux/store'

const Profile: React.FC<{ id: string; type: string }> = ({ id, type }) => {
  const { t } = useTranslation()
  const [showContent, setShowContent] = useState<boolean | null>(null)
  const { access_count, isBeneficiary, accessPer } = useSelector(
    (state: RootState) => state.user
  )
  const [profile, setProfile] = useState({
    content: '',
    businessSector: '',
    activities: [],
    certification: [],
    zones: [],
    products: [],
    origin: '',
    countryCount: '',
    provinceCount: '',
  })
  const [coverageValue, setCoverageValue] = useState('')
  useEffect(() => {
    const fetchData = async () => {
      const response: any | { message: string; status: string } =
        await getCompanyProfileData(id)
      if (response?.message === 'Access Exhausted') {
        setShowContent(false)
      } else {
        setShowContent(true)

        setProfile({
          ...profile,
          content: response?.about_us?.description?.content || 'Not Available',
          businessSector:
            response?.company_details?.business_sector || 'Not Available',
          activities: response?.company_details?.activities || [],
          certification: response?.company_details?.certifications || [],
          zones: response?.company_details?.commercial_zones || [],
          products: response?.products || [],
          origin: response?.company_details?.origin?.name || '-',
          countryCount: response?.countries,
          provinceCount: response?.provinces,
        })
        if (Number(profile?.countryCount) > 0) {
          const provinceInfo =
            Number(profile?.provinceCount) > 0
              ? `, ${profile?.provinceCount} ${t('Region/Province')}`
              : ''
          setCoverageValue(
            `${profile?.countryCount} ${t('Countries')} ${provinceInfo}}`
          )
        } else {
          setCoverageValue('-')
        }
      }
    }

    fetchData()
  }, [])
  if (showContent === null) {
    return <Skeleton className="w-[952px] h-[500px]" />
  }
  return (
    <main>
      {!showContent && (
        <CompanyBlocker
          access_count={access_count}
          isBeneficiary={isBeneficiary}
          accessPer={accessPer}
        />
      )}
      <div className="w-[952px] shadow-[0_0_6px_0_rgba(0,0,0,0.16)] rounded mb-3 mobile-width">
        <div className="text-base font-ns_bold border-b border-solid border-input p-6 py-5">
          {t('Company Details')}
        </div>
        <div className="p-6 pb-7">
          <div className="grid grid-cols-2 mobile:grid-cols-1 gap-7 mobile:gap-4 pb-3">
            <div>
              <h1 className="text-sm text-light_title font-ns_semibold">
                {t('Business Sector')}
              </h1>
              <p className="text-base text-grey_forty font-ns_semibold">
                {profile?.businessSector}
              </p>
            </div>
            <div>
              <h1 className="text-sm text-light_title font-ns_semibold">
                {t('Activities')}
              </h1>
              <p className="text-base text-grey_forty font-ns_semibold flex flex-row">
                {profile?.activities?.length > 0
                  ? String(profile?.activities.join(', '))
                  : '-'}
              </p>
            </div>
            <div className="">
              <h1 className="text-sm text-light_title font-ns_semibold">
                {t('Origin')}
              </h1>
              <p className="text-base text-grey_forty font-ns_semibold">
                {profile?.origin}
              </p>
            </div>
            <div>
              <h1 className="text-sm text-light_title font-ns_semibold">
                {t('Certification')}
              </h1>
              <p className="text-base text-grey_forty font-ns_semibold">
                {profile?.certification?.length > 0
                  ? String(profile?.certification.join(', '))
                  : '-'}
              </p>
            </div>
            {type === 'Fruits' && (
              <div>
                <h1 className="text-sm text-light_title font-ns_semibold">
                  {t('Our Commercial Zones')}
                </h1>
                <p className="flex text-base text-grey_forty font-ns_semibold">
                  {profile?.zones?.length > 0
                    ? String(profile?.zones.join(', '))
                    : '-'}
                </p>
              </div>
            )}
            {type === 'Transport' && (
              <div>
                <h1 className="text-sm text-light_title font-ns_semibold">
                  {t('Coverage Areas')}
                </h1>
                <p className="flex text-base text-grey_forty font-ns_semibold">
                  {coverageValue}
                </p>
              </div>
            )}
            {type === 'Prepost' && (
              <div>
                <h1 className="text-sm text-light_title font-ns_semibold">
                  {t('Products/Service')}
                </h1>
                <p className="text-base text-grey_forty font-ns_semibold">
                  {profile?.products?.length > 0
                    ? profile?.products?.map((item: any, index: number) => (
                        <span key={`p${index}`}>
                          {item.name}
                          {index !== profile.products.length - 1 && ', '}
                        </span>
                      ))
                    : '-'}
                </p>
              </div>
            )}
          </div>
          <div>
            <h1 className="py-3 pt-6 text-base text-text_dark border-b border-solid font-ns_bold border-input">
              {t('About Us')}
            </h1>
            <p
              className={`text-gray_text text-base pt-5 py-8 px-0 ${profile?.content === 'Not Available' && 'flex justify-center items-center m-5 mt-10'}`}
            >
              {t(profile?.content)}
            </p>
          </div>
        </div>
      </div>
      {(type === 'Fruits' || type === 'Transport') && (
        <ProductOrServiceSection
          products={profile?.products}
          type={type === 'Fruits' ? 'Products' : 'Services'}
        />
      )}
    </main>
  )
}

export default Profile
